'use client'

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { settingsService } from '@/lib/api/services/settings'

type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeContextType {
  theme: ThemeMode
  resolvedTheme: 'light' | 'dark'
  setTheme: (theme: ThemeMode) => Promise<void>
  isLoading: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

function applyTheme(theme: 'light' | 'dark') {
  const root = document.documentElement
  if (theme === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>('system')
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light')
  const [isLoading, setIsLoading] = useState(true)

  // Resolve the actual theme based on preference
  const resolveTheme = useCallback((themeMode: ThemeMode): 'light' | 'dark' => {
    if (themeMode === 'system') {
      return getSystemTheme()
    }
    return themeMode
  }, [])

  // Load initial theme from settings
  useEffect(() => {
    const loadTheme = async () => {
      // First, apply theme from localStorage immediately to avoid flash
      const savedTheme = localStorage.getItem('theme') as ThemeMode
      if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
        setThemeState(savedTheme)
        const resolved = resolveTheme(savedTheme)
        setResolvedTheme(resolved)
        applyTheme(resolved)
      } else {
        const resolved = resolveTheme('system')
        setResolvedTheme(resolved)
        applyTheme(resolved)
      }

      // Only try API if user is authenticated (has access token)
      const hasToken = typeof window !== 'undefined' && localStorage.getItem('access_token')
      if (hasToken) {
        try {
          const settings = await settingsService.getAppearanceSettings()
          const apiTheme = (settings.theme as ThemeMode) || 'system'
          if (apiTheme !== savedTheme) {
            setThemeState(apiTheme)
            const resolved = resolveTheme(apiTheme)
            setResolvedTheme(resolved)
            applyTheme(resolved)
            localStorage.setItem('theme', apiTheme)
          }
        } catch (_error) {
          // API call failed, keep using localStorage value
        }
      }

      setIsLoading(false)
    }

    loadTheme()
  }, [resolveTheme])

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      if (theme === 'system') {
        const resolved = getSystemTheme()
        setResolvedTheme(resolved)
        applyTheme(resolved)
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  // Set theme and save to API
  const setTheme = useCallback(async (newTheme: ThemeMode) => {
    setThemeState(newTheme)
    const resolved = resolveTheme(newTheme)
    setResolvedTheme(resolved)
    applyTheme(resolved)

    // Save to localStorage
    localStorage.setItem('theme', newTheme)

    // Save to API only if authenticated
    const hasToken = typeof window !== 'undefined' && localStorage.getItem('access_token')
    if (hasToken) {
      try {
        await settingsService.updateAppearanceSettings({ theme: newTheme })
      } catch (_error) {
        // Ignore API errors - theme is already applied locally
      }
    }
  }, [resolveTheme])

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
